import React, { useState, useEffect, useCallback } from "react";

import {
  FaHandHoldingHeart,
  FaUniversity,
  FaGift,
  FaUsers,
  FaCheckCircle,
  FaGopuram,
  FaSpinner,
  FaQrcode,
  FaUpload,
  FaCopy,
  FaCheck,
  FaReceipt,
} from "react-icons/fa";

import { useNavigate } from "react-router-dom";

import "../styles/Donate.css";
import { API_ENDPOINTS } from "../config/api";
import qrCodeImg from "../assets/qr-code.jpeg";
import DonationReceipt from "../components/DonationReceipt";

const Donate = () => {
  const navigate = useNavigate();

  /* ================= API ================= */
  const API = API_ENDPOINTS.DONATIONS;

  /* ================= STATES ================= */
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userLoading, setUserLoading] = useState(true);

  const [amount, setAmount] = useState(501);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    donorName: "",
    email: "",
    phoneNumber: "",
    address: "",
    purpose: "",
    transactionId: "",
  });

  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState("");

  const [isPhoneLocked, setIsPhoneLocked] = useState(false);

  const [donations, setDonations] = useState([]);
  const [donationsLoading, setDonationsLoading] = useState(true);
  const [copiedUpi, setCopiedUpi] = useState(false);
  const [receiptDonation, setReceiptDonation] = useState(null);

  const templeUpiId = "gnanasaiguruji@okhdfcbank";
  const templePayee = "Parasuram Guruji S";

  const handleCopyUpi = () => {
    navigator.clipboard.writeText(templeUpiId);
    setCopiedUpi(true);
    setTimeout(() => setCopiedUpi(false), 2000);
  };

  const donationAmounts = [101, 251, 501, 1001, 5001];

  /* ================= FETCH USER & DONATIONS ================= */

  const fetchUserProfile = useCallback(async () => {
    if (!token) {
      setUserLoading(false);
      return;
    }
    try {
      setUserLoading(true);
      const res = await fetch(`${API_ENDPOINTS.USER_PROFILE}/me`, {
        method: "GET",
        headers: {
          Authorization: token,
        },
      });
      const data = await res.json();
      if (res.ok) {
        setFormData((prev) => ({
          ...prev,
          donorName: data.fullName || "",
          email: data.email || "",
          phoneNumber: data.mobile || "",
        }));
        if (data.mobile) {
          setIsPhoneLocked(true);
        }
      } else {
        localStorage.removeItem("token");
        setToken(null);
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
    } finally {
      setUserLoading(false);
    }
  }, [token]);

  const fetchDonations = useCallback(async () => {
    try {
      setDonationsLoading(true);
      const res = await fetch(API);
      const data = await res.json();
      if (res.ok) {
        setDonations(data);
      }
    } catch (err) {
      console.error("Error fetching donations:", err);
    } finally {
      setDonationsLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (token) {
      fetchUserProfile();
      fetchDonations();
    } else {
      setUserLoading(false);
    }
  }, [token, fetchUserProfile, fetchDonations]);

  /* ================= HANDLE INPUT ================= */

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshotFile(file);
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  /* ================= HANDLE DONATION ================= */

  const handleDonate = async () => {
    try {
      /* VALIDATION */
      if (
        !formData.donorName ||
        !formData.email ||
        !formData.phoneNumber ||
        !amount
      ) {
        alert("Please fill all required donor fields");
        return;
      }
      if (!formData.purpose.trim()) {
        alert("Please specify a donation purpose");
        return;
      }
      if (!formData.transactionId.trim()) {
        alert("Please enter the Payment ID / Transaction UTR Number");
        return;
      }
      if (!screenshotFile) {
        alert("Please upload your payment screenshot image");
        return;
      }

      setLoading(true);

      /* 1. UPLOAD SCREENSHOT IMAGE */
      const uploadData = new FormData();
      uploadData.append("image", screenshotFile);

      const uploadRes = await fetch(API_ENDPOINTS.UPLOAD, {
        method: "POST",
        body: uploadData,
      });

      const uploadResult = await uploadRes.json();

      if (!uploadRes.ok || !uploadResult.imageUrl) {
        alert("Failed to upload payment screenshot. Please try again.");
        setLoading(false);
        return;
      }

      const paymentScreenshotUrl = uploadResult.imageUrl;

      /* 2. SUBMIT DONATION DATA TO BACKEND */
      const donationRes = await fetch(API_ENDPOINTS.DONATIONS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          donorName: formData.donorName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          address: formData.address || "",
          amount: Number(amount),
          purpose: formData.purpose || "General Donation",
          transactionId: formData.transactionId,
          paymentScreenshot: paymentScreenshotUrl,
          paymentMethod: "QR Code / UPI",
          status: "Pending",
        }),
      });

      const donationData = await donationRes.json();

      if (donationRes.ok && donationData.success) {
        const newlyCreated = donationData.donation || {
          donorName: formData.donorName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          address: formData.address || "Ambattur, Chennai",
          amount: Number(amount),
          purpose: formData.purpose || "General Donation",
          transactionId: formData.transactionId,
          paymentMethod: "QR Code / UPI",
          receiptNumber: "7900",
          createdAt: new Date().toISOString(),
        };

        setReceiptDonation(newlyCreated);

        alert(
          `🙏 Thank you ${formData.donorName}! Your donation details have been submitted. You can now view and print your official Mandir receipt.`
        );

        setFormData((prev) => ({
          ...prev,
          purpose: "",
          transactionId: "",
        }));
        setScreenshotFile(null);
        setScreenshotPreview("");
        setAmount(501);
        fetchDonations();
      } else {
        alert(donationData.message || "Failed to submit donation.");
      }
    } catch (err) {
      console.error("Donation Error:", err);
      alert("An unexpected error occurred while submitting your donation.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= RENDER LOGIN GATE ================= */
  if (!token) {
    return (
      <div className="donate-page auth-required-page">
        <div className="auth-required-card">
          <div className="auth-icon-wrapper">
            <FaHandHoldingHeart className="auth-icon" />
          </div>
          <h1>Join Sai Baba Seva</h1>
          <p>
            Please log in to make a contribution and view the list of
            donations.
          </p>
          <button
            className="auth-login-btn"
            onClick={() => navigate("/account")}
          >
            Login / Create Account
          </button>
        </div>
      </div>
    );
  }

  /* ================= RENDER LOADING STATE ================= */
  if (userLoading) {
    return (
      <div className="donate-page loading-screen">
        <div className="spinner-wrapper">
          <FaSpinner className="spin-icon" />
          <p>Loading your details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="donate-page">
      {/* ================= HEADER ================= */}

      <div className="hero-content">
        <h1>Donate for Sai Baba Seva</h1>

        <p>
          Your generous contribution supports annadanam, temple poojas,
          spiritual services, festivals, and charitable activities for devotees.
        </p>

        <a href="#payment-section" className="hero-btn">
          <FaHandHoldingHeart />
          Contribute Now
        </a>
      </div>

      {/* ================= MAIN WRAPPER ================= */}

      <div className="donate-wrapper">
        {/* ================= LEFT SECTION ================= */}

        <div className="donate-left">
          {/* ================= QR CODE PAYMENT CARD ================= */}
          <div className="donate-card qr-card-wrapper">
            <div className="card-title">
              <FaQrcode />
              <h2>Sri Gnana Sai Baba QR Code Payment</h2>
            </div>

            <div className="qr-container-box">
              <div className="qr-image-frame">
                <img
                  src={qrCodeImg}
                  alt="Sri Gnana Sai Baba QR Code"
                  className="qr-code-img"
                />
              </div>

              <div className="qr-instructions">
                <h3>How to Donate via QR Code:</h3>

                <div
                  style={{
                    background: "#fff8eb",
                    border: "1.5px solid #f6d89b",
                    borderRadius: "12px",
                    padding: "10px 14px",
                    marginBottom: "14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <div style={{ fontSize: "13px", color: "#854d0e", fontWeight: "700" }}>
                    Payee: <span style={{ color: "#1e3a8a", fontWeight: "800" }}>{templePayee}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "14px", fontWeight: "800", color: "#0f2d5c", letterSpacing: "0.3px" }}>
                      {templeUpiId}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyUpi}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        background: copiedUpi ? "#dcfce7" : "#0f2d5c",
                        color: copiedUpi ? "#15803d" : "#f5c542",
                        border: "none",
                        borderRadius: "6px",
                        padding: "5px 10px",
                        fontSize: "12px",
                        fontWeight: "700",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {copiedUpi ? <FaCheck /> : <FaCopy />}
                      <span>{copiedUpi ? "Copied" : "Copy UPI"}</span>
                    </button>
                  </div>
                </div>

                <ol>
                  <li>
                    Open <strong>Google Pay</strong>, <strong>PhonePe</strong>,{" "}
                    <strong>Paytm</strong>, or any <strong>UPI app</strong>.
                  </li>
                  <li>Scan the Sri Gnana Sai Baba QR Code shown here (or send to UPI: <strong>{templeUpiId}</strong>).</li>
                  <li>Enter your donation amount and complete the payment.</li>
                  <li>
                    Copy the <strong>Transaction ID / Payment UTR Number</strong>.
                  </li>
                  <li>
                    Take a screenshot of the payment receipt and upload it in the
                    form.
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* ================= TEMPLE SEVA ================= */}

          <div className="donate-card">
            <div className="card-title">
              <FaGopuram />
              <h2>Temple Seva Activities</h2>
            </div>

            <div className="seva-grid">
              <div className="seva-box">
                <FaGift />
                <h3>Annadanam</h3>
                <p>
                  Support free food distribution for devotees and visitors.
                </p>
              </div>

              <div className="seva-box">
                <FaGopuram />
                <h3>Daily Pooja</h3>
                <p>
                  Help conduct daily poojas, aarti, and spiritual rituals.
                </p>
              </div>

              <div className="seva-box">
                <FaUsers />
                <h3>Festival Support</h3>
                <p>
                  Celebrate Sai Baba festivals and temple events beautifully.
                </p>
              </div>

              <div className="seva-box">
                <FaUniversity />
                <h3>Temple Maintenance</h3>
                <p>
                  Support temple cleaning, decoration, and maintenance services.
                </p>
              </div>
            </div>
          </div>

          {/* ================= BLESSING ================= */}

          <div className="blessing-card">
            <h2>Sai Baba Blessings</h2>
            <p>
              “Even the smallest act of kindness and donation offered with
              devotion reaches Sai Baba and returns as divine blessings.”
            </p>
          </div>

          {/* ================= IMPACT ================= */}

          <div className="donate-card">
            <div className="card-title">
              <FaCheckCircle />
              <h2>Your Contribution Helps</h2>
            </div>

            <div className="impact-list">
              <div className="impact-item">
                <span>✔</span>
                <p>Daily Annadanam for devotees and visitors</p>
              </div>

              <div className="impact-item">
                <span>✔</span>
                <p>Temple maintenance and spiritual development</p>
              </div>

              <div className="impact-item">
                <span>✔</span>
                <p>Festival celebrations and temple decorations</p>
              </div>

              <div className="impact-item">
                <span>✔</span>
                <p>Supporting seva activities and social services</p>
              </div>
            </div>
          </div>

          {/* ================= DONATIONS LIST ================= */}

          <div className="donate-card donations-list-card">
            <div className="card-title">
              <FaUsers />
              <h2>Recent Contributions</h2>
            </div>

            {donationsLoading ? (
              <div className="list-loading">
                <FaSpinner className="spin-icon" /> Loading contributions...
              </div>
            ) : donations.length === 0 ? (
              <p className="no-donations">
                No donations submitted yet. Be the first to contribute!
              </p>
            ) : (
              <div className="donations-table-wrapper">
                <table className="donations-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Amount</th>
                      <th>Donation Purpose</th>
                      <th>Status</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map((donation, index) => (
                      <tr key={donation._id || index}>
                        <td className="donor-name">{donation.donorName}</td>
                        <td className="donor-amount">₹{donation.amount}</td>
                        <td className="donor-purpose">
                          {donation.purpose || "General Donation"}
                        </td>
                        <td>
                          <span
                            className={`donor-status-badge ${
                              donation.status?.toLowerCase() === "approved" ||
                              donation.status?.toLowerCase() === "success"
                                ? "approved"
                                : donation.status?.toLowerCase() === "rejected"
                                ? "rejected"
                                : "pending"
                            }`}
                          >
                            {donation.status || "Pending"}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="receipt-table-btn"
                            onClick={() => setReceiptDonation(donation)}
                            title="View / Print Mandir Receipt"
                          >
                            <FaReceipt /> Receipt
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ================= RIGHT SECTION ================= */}

        <div className="donate-right" id="payment-section">
          {/* ================= DONATION FORM ================= */}

          <div className="payment-card">
            <h2>Submit Donation Details</h2>

            {/* NAME */}

            <div className="custom-amount">
              <label>Full Name</label>
              <input
                type="text"
                name="donorName"
                placeholder="Your full name"
                value={formData.donorName}
                disabled
                readOnly
                className="locked-input"
              />
            </div>

            {/* EMAIL */}

            <div className="custom-amount">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={formData.email}
                disabled
                readOnly
                className="locked-input"
              />
            </div>

            {/* PHONE NUMBER */}

            <div className="custom-amount">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phoneNumber"
                placeholder="Your phone number"
                value={formData.phoneNumber}
                onChange={handleChange}
                disabled={isPhoneLocked}
                readOnly={isPhoneLocked}
                className={isPhoneLocked ? "locked-input" : ""}
                required
              />
            </div>

            {/* ADDRESS (FOR RECEIPT) */}

            <div className="custom-amount">
              <label>Address / City (For Official Receipt)</label>
              <input
                type="text"
                name="address"
                placeholder="Enter address or locality (e.g. Ambattur, Chennai)"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            {/* PURPOSE */}

            <div className="custom-amount">
              <label>Donation Purpose</label>
              <input
                type="text"
                name="purpose"
                placeholder="Enter donation purpose (e.g. Annadanam, Abhishekam)"
                value={formData.purpose}
                onChange={handleChange}
                required
              />
            </div>

            {/* QUICK AMOUNTS */}

            <div className="amount-buttons">
              {donationAmounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  className={amount === Number(amt) ? "active-amount" : ""}
                  onClick={() => setAmount(amt)}
                >
                  ₹{amt}
                </button>
              ))}
            </div>

            {/* CUSTOM AMOUNT */}

            <div className="custom-amount">
              <label>Donation Amount (₹)</label>
              <input
                type="number"
                placeholder="Enter donation amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>

            {/* TRANSACTION ID / UTR */}

            <div className="custom-amount">
              <label>Payment / Transaction ID (UTR Number) *</label>
              <input
                type="text"
                name="transactionId"
                placeholder="Enter Payment ID / UTR (e.g. 423456789012)"
                value={formData.transactionId}
                onChange={handleChange}
                required
              />
            </div>

            {/* PAYMENT SCREENSHOT UPLOAD */}

            <div className="custom-amount screenshot-upload-wrapper">
              <label>Upload Payment Screenshot *</label>
              <div className="file-upload-box">
                <input
                  type="file"
                  id="screenshot-input"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  style={{ display: "none" }}
                  required
                />
                <label htmlFor="screenshot-input" className="file-upload-btn">
                  <FaUpload /> Select Screenshot Image
                </label>

                {screenshotPreview ? (
                  <div className="screenshot-preview-container">
                    <img
                      src={screenshotPreview}
                      alt="Payment Screenshot Preview"
                      className="screenshot-preview-img"
                    />
                    <p className="file-name-text">{screenshotFile?.name}</p>
                  </div>
                ) : (
                  <p className="no-file-text">No image selected</p>
                )}
              </div>
            </div>

            {/* DONATE BUTTON */}

            <button
              className="donate-btn"
              onClick={handleDonate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaSpinner className="spin-icon" />
                  Submitting Payment Details...
                </>
              ) : (
                <>
                  <FaCheckCircle /> Submit Donation ₹{amount}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* MANDIR OFFICIAL DONATION RECEIPT MODAL */}
      <DonationReceipt
        isOpen={Boolean(receiptDonation)}
        donation={receiptDonation}
        onClose={() => setReceiptDonation(null)}
      />
    </div>
  );
};

export default Donate;